import {
  EisFundStartup,
  InvestmentStatus,
  TaxBenefit,
} from "@components/EisFund/types";

const EisFundActionTypes = {
  FETCH_EIS_FUND_INVESTMENTS: "FETCH_EIS_FUND_INVESTMENTS",
  LOAD_MORE_EIS_FUND_INVESTMENTS: "LOAD_MORE_EIS_FUND_INVESTMENTS",
  CLEAR_EIS_FUND_CACHE: "CLEAR_EIS_FUND_CACHE",
};

export default EisFundActionTypes;

// ============================================================================
// Status Types
// ============================================================================

/**
 * Request status for tracking loading states
 */
export type RequestStatus = "idle" | "loading" | "loadingMore" | "success" | "error";

// ============================================================================
// Pagination
// ============================================================================

export interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
}

// ============================================================================
// Commitment Level (innermost cache unit)
// ============================================================================

/**
 * Cached investments for a specific commitment.
 * Path: eisFund.deals[dealId].commitments[commitmentId]
 */
export interface CommitmentInvestmentsCache {
  /** The investment items for this commitment */
  investments: EisFundStartup[];
  /** Pagination state */
  pagination: PaginationState;
  /** Current request status */
  status: RequestStatus;
  /** Error message if status is 'error' */
  error: string | null;
  /** Timestamp of last successful fetch */
  lastFetched: number | null;
  /** Serialized filter key for cache invalidation */
  filterKey: string;
}

// ============================================================================
// Deal Level
// ============================================================================

/**
 * A deal containing its commitments.
 * Path: eisFund.deals[dealId]
 */
export interface DealCache {
  commitments: {
    [commitmentId: string]: CommitmentInvestmentsCache;
  };
}

// ============================================================================
// Root State
// ============================================================================

/**
 * EIS Fund Redux state.
 *
 * Structure:
 * ```
 * eisFund
 *   └── deals
 *         └── [dealId]
 *               └── commitments
 *                     └── [commitmentId]
 *                           ├── investments: EisFundStartup[]
 *                           ├── pagination: PaginationState
 *                           ├── status: RequestStatus
 *                           ├── error: string | null
 *                           ├── lastFetched: number | null
 *                           └── filterKey: string
 * ```
 */
export interface EisFundState {
  deals: {
    [dealId: string]: DealCache;
  };
}

// ============================================================================
// Action Payloads
// ============================================================================

/**
 * Parameters for fetching EIS Fund investments
 */
export interface FetchEisFundInvestmentsPayload {
  dealId: string;
  commitmentId: string;
  dealName?: string;
  taxBenefit?: TaxBenefit | "all";
  statusLabelNames?: InvestmentStatus[];
  pageSize?: number;
}

/**
 * Parameters for loading more (pagination)
 */
export interface LoadMoreEisFundInvestmentsPayload
  extends FetchEisFundInvestmentsPayload {
  page: number;
}

/**
 * Fulfilled action payload structure
 */
export interface EisFundFulfilledPayload {
  dealId: string;
  commitmentId: string;
  investments: EisFundStartup[];
  pagination: PaginationState;
  filterKey: string;
}

/**
 * Rejected action payload structure
 */
export interface EisFundRejectedPayload {
  dealId: string;
  commitmentId: string;
  error: string;
}

// ============================================================================
// Initial States (exported for use in reducer and selectors)
// ============================================================================

export const INITIAL_PAGINATION: PaginationState = {
  currentPage: 1,
  pageSize: 20,
  totalCount: 0,
  totalPages: 0,
  hasNextPage: true,
};

export const INITIAL_COMMITMENT_CACHE: CommitmentInvestmentsCache = {
  investments: [],
  pagination: INITIAL_PAGINATION,
  status: "idle",
  error: null,
  lastFetched: null,
  filterKey: "",
};

export const INITIAL_EIS_FUND_STATE: EisFundState = {
  deals: {},
};
